if (!customElements.get('quick-cart-drawer')) {
  class QuickCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._coreListenersBound = false;
      this.toggleState = false;
      this._openAsModal = false;
      this._cartDrawerWasOpen = false;
      if (Shopify.designMode) {
        window.addEventListener('shopify:section:load', this.init.bind(this));
        this.parentElement.addEventListener('shopify:section:select', () => this.open());
        this.parentElement.addEventListener('shopify:section:deselect', () => this.close());
      }
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Guard: do NOT reset state if the drawer is currently open
      if (!this.classList.contains('is--open') && !this.classList.contains('is--modal')) {
        this.toggleState = false;
      }

      if (!this._coreListenersBound) {
        this._coreListenersBound = true;

        // Close button
        this.querySelector('.button--close').addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });

        // Backdrop click closes
        this.querySelector('.quick-cart-drawer__backdrop').addEventListener('click', (e) => {
          e.stopPropagation();
          this.close();
        });

        // Desktop overlay: click outside closes
        document.addEventListener('click', (e) => {
          if (!this.toggleState) return;
          if (this.classList.contains('is--desktop-overlay')) {
            var blocks = this.querySelector('.quick-cart-drawer__blocks');
            if (blocks && !blocks.contains(e.target) && !e.target.closest('.quick-cart-drawer__trigger')) {
              this.close();
            }
          }
        });

        // Modal mode: click on blocks container (outside main) closes
        this.querySelector('.quick-cart-drawer__blocks').addEventListener('click', (e) => {
          if (!this.classList.contains('is--modal')) return;
          var main = this.querySelector('.quick-cart-drawer__main');
          if (main && !main.contains(e.target)) {
            this.close();
          }
        });

        // Escape key
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.toggleState) {
            this.close();
          }
          if (e.key === 'Escape' && this._activeOverlay) {
            this.closeInlineOverlay();
          }
        });

        // Tab trap
        this.querySelector('.quick-cart-drawer__blocks').addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.toggleState) {
            this.close();
          }
          if (this.toggleState) {
            var focusableElements = this.querySelectorAll(
              'button, [href], input, select, label, textarea, [tabindex]:not([tabindex="-1"])'
            );
            var firstFocusableElement = focusableElements[0];
            var lastFocusableElement = focusableElements[focusableElements.length - 1];
            if (e.key !== 'Tab') return;
            if (e.shiftKey) {
              if (document.activeElement === firstFocusableElement) {
                e.preventDefault();
                lastFocusableElement.focus();
              }
            } else {
              if (document.activeElement === lastFocusableElement) {
                e.preventDefault();
                firstFocusableElement.focus();
              }
            }
          }
        });

        this._initMutationObserver();
      }

      this._bindNewTriggers();
    }

    _initMutationObserver() {
      var self = this;
      var observer = new MutationObserver(function() {
        self._bindNewTriggers();
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    _bindNewTriggers() {
      var self = this;
      document.querySelectorAll('.quick-cart-drawer__trigger').forEach(function(el) {
        if (!el._qcdBound) {
          el.addEventListener('click', function(event) {
            self.fetchProductForQuickCartDrawer(event);
          });
          el._qcdBound = true;
        }
      });
    }

    async fetchProductForQuickCartDrawer(event) {
      var triggerBtn = event.target.closest('.quick-cart-drawer__trigger');
      if (!triggerBtn) return;

      // If modal is already open, close it first
      if (this.toggleState && this.classList.contains('is--modal')) {
        this.close();
        await new Promise(function(r) { setTimeout(r, 50); });
      }

      // Stop autoplay (safely)
      var slider = triggerBtn.closest('card-product-slider');
      if (slider) {
        try {
          if (slider.slider && slider.slider.autoplay) slider.slider.autoplay.stop();
          slider.classList.add('product--open-on-quick-cart');
        } catch(e) {}
      }

      var productCard = triggerBtn.closest('product-card');
      var isRecommendations = triggerBtn.classList.contains('quick-cart-drawer__trigger--recommendations');

      // Product cards: check inline overlay first
      if (productCard) {
        var inlineOverlay = productCard.querySelector('.quick-size-overlay');
        if (inlineOverlay) {
          this.openInlineOverlay(inlineOverlay, productCard);
          return;
        }
      } else if (!isRecommendations) {
        return;
      }

      this._triggerCard = productCard;
      this._openAsModal = isRecommendations;

      var trigger = triggerBtn.closest('[data-product-url]') || triggerBtn;
      var productUrl = trigger.dataset.productUrl;
      if (!productUrl) return;

      trigger.classList.add('is--loading');
      var fetchSuccess = false;

      try {
        var fetchPrefix = '';
        if (window.Shopify.routes.root.indexOf('/' + window.Shopify.locale + '/') !== -1) {
          fetchPrefix = '/' + window.Shopify.locale;
        }

        var resp = await fetch(fetchPrefix + productUrl + '/?view=quick-cart');
        if (!resp.ok) return;

        var html = await resp.text();
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var productEl = tmp.querySelector('.quick-cart-product');
        if (!productEl) return;

        // --- Auto-add check: if product is simple (1 variant or no real choices), add directly ---
        // Skip auto-add if _forceModal flag is set (user tapped card image, not + button)
        if (isRecommendations && !event._forceModal) {
          var variantsScript = productEl.querySelector('[data-product-variants-json]');
          if (variantsScript) {
            try {
              var variants = JSON.parse(variantsScript.textContent);
              var availableVariants = variants.filter(function(v) { return v.available; });
              var isSimple = false;

              // Only 1 available variant = simple
              if (availableVariants.length === 1) {
                isSimple = true;
              }
              // Or all available variants have same options (e.g. "Default Title" / single color + single size)
              else if (availableVariants.length > 0) {
                var uniqueOpt1 = [];
                var uniqueOpt2 = [];
                availableVariants.forEach(function(v) {
                  if (v.option1 && uniqueOpt1.indexOf(v.option1) === -1) uniqueOpt1.push(v.option1);
                  if (v.option2 && uniqueOpt2.indexOf(v.option2) === -1) uniqueOpt2.push(v.option2);
                });
                // 1 option1 value and 0 or 1 option2 values = no real choices
                if (uniqueOpt1.length <= 1 && uniqueOpt2.length <= 1) {
                  isSimple = true;
                }
              }

              if (isSimple && availableVariants.length > 0) {
                // Direct add to cart — no modal needed
                trigger.classList.remove('is--loading');
                this._openAsModal = false;
                var preorderEl = productEl.querySelector('[data-has-pre-order]');
                var isPreorder = !!preorderEl;
                var shipDate = preorderEl ? (preorderEl.getAttribute('data-preorder-ship-date') || '') : '';
                await this._directAddToCart(availableVariants[0].id, triggerBtn, isPreorder, shipDate);
                return;
              }
            } catch(e) {}
          }
        }

        var mainEl = this.querySelector('.quick-cart-drawer__main');
        if (!mainEl) return;

        // Ensure header is NOT in main before clearing
        this._ensureHeaderInBlocks();

        mainEl.innerHTML = '';
        mainEl.append(productEl);
        fetchSuccess = true;
      } catch (err) {
        // fetch failed silently
      } finally {
        trigger.classList.remove('is--loading');
        if (fetchSuccess) {
          this.open();
        }
      }
    }

    /**
     * Directly add a variant to cart without showing any modal.
     * Shows "ADDED" feedback on the trigger button.
     */
    async _directAddToCart(variantId, triggerBtn, isPreorder, shipDate) {
      triggerBtn.classList.add('is--loading');
      try {
        var addBody = { id: variantId, quantity: 1 };
        if (isPreorder) {
          var formattedDate = '';
          if (shipDate) {
            var d = new Date(shipDate + 'T00:00:00');
            if (!isNaN(d.getTime())) {
              formattedDate = (d.getMonth()+1) + '/' + d.getDate() + '/' + String(d.getFullYear()).slice(-2);
            }
          }
          var preorderValue = formattedDate ? 'ETA: ' + formattedDate : '✓';
          addBody.properties = { 'Preorder': preorderValue };
        }
        var addResp = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addBody)
        });
        var data = await addResp.json();
        if (!data.id && !data.items) return;

        // Update cart UI
        if (typeof updateCartCounters === 'function') updateCartCounters();
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          var sectionsResp = await fetch(window.location.pathname + '?sections=cart-drawer,cart-counter');
          var sections = await sectionsResp.json();
          cartDrawer.renderContents({ sections }, false);
          if (typeof updateCartCounters === 'function') updateCartCounters();
          if (typeof updateFreeShipping === 'function') updateFreeShipping();
          // Manually init recommendations if connectedCallback didn't fire
          setTimeout(function() {
            var recs = document.querySelector('#CartDrawer-Body product-recommendations');
            if (recs && !recs._loaded && typeof recs.performRecommendations === 'function') {
              recs.performRecommendations();
            }
          }, 200);
        }

        // Trigger sync so the button shows "ADDED"
        document.dispatchEvent(new Event('quick-cart:item-added'));
      } catch(e) {
        // silently fail
      } finally {
        triggerBtn.classList.remove('is--loading');
      }
    }

    _ensureHeaderInBlocks() {
      var header = this.querySelector('.quick-cart-drawer__header');
      var main = this.querySelector('.quick-cart-drawer__main');
      var blocks = this.querySelector('.quick-cart-drawer__blocks');
      if (header && main && blocks && header.parentElement === main) {
        blocks.insertBefore(header, main);
      }
    }

    openInlineOverlay(overlay, productCard) {
      document.querySelectorAll('.quick-size-overlay.is-active').forEach(function(el) {
        el.classList.remove('is-active');
      });
      overlay.querySelectorAll('.quick-size-overlay__btn').forEach(function(btn) {
        btn.style.borderColor = '';
        btn.style.borderWidth = '';
      });

      if (window.innerWidth < 750) {
        if (!overlay.dataset.originalParent) {
          overlay.dataset.originalParent = 'true';
          overlay._originalParent = overlay.parentElement;
          overlay._originalNextSibling = overlay.nextSibling;
        }
        document.body.appendChild(overlay);
      } else {
        if (overlay._originalParent && overlay.parentElement === document.body) {
          if (overlay._originalNextSibling) {
            overlay._originalParent.insertBefore(overlay, overlay._originalNextSibling);
          } else {
            overlay._originalParent.appendChild(overlay);
          }
        }
      }

      overlay.classList.add('is-active');
      this._activeOverlay = overlay;
      this._activeProductCard = productCard;

      var variantsJson = productCard.querySelector('[data-product-variants-json]');
      var variants = [];
      if (variantsJson) {
        try { variants = JSON.parse(variantsJson.textContent); } catch(e) {}
      }

      var checkedColorInput = productCard.querySelector('input[type="radio"]:checked');
      var currentColor = null;
      if (checkedColorInput) {
        currentColor = checkedColorInput.value;
      }

      var closeBtn = overlay.querySelector('.quick-size-overlay__close');
      if (closeBtn && !closeBtn._bound) {
        closeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closeInlineOverlay();
        });
        closeBtn._bound = true;
      }

      var self = this;
      overlay.querySelectorAll('.quick-size-overlay__btn').forEach(function(btn) {
        if (btn._bound) return;

        btn.addEventListener('click', function(e) {
          e.stopPropagation();
          e.preventDefault();

          var sizeValue = btn.dataset.sizeValue;
          var optionPosition = parseInt(btn.dataset.optionPosition);
          if (!sizeValue) return;

          var matchedVariant = null;
          for (var j = 0; j < variants.length; j++) {
            var v = variants[j];
            if (!v.available) continue;
            var optionKey = 'option' + optionPosition;
            if (v[optionKey] === sizeValue) {
              if (currentColor && optionPosition !== 1 && v.option1 !== currentColor) continue;
              matchedVariant = v;
              break;
            }
          }
          if (!matchedVariant) {
            for (var j = 0; j < variants.length; j++) {
              var v = variants[j];
              if (!v.available) continue;
              if (v['option' + optionPosition] === sizeValue) {
                matchedVariant = v;
                break;
              }
            }
          }
          if (!matchedVariant) return;

          overlay.querySelectorAll('.quick-size-overlay__btn').forEach(function(b) {
            b.style.borderColor = '';
            b.style.borderWidth = '';
          });
          btn.style.borderColor = '#000';
          btn.style.borderWidth = '2px';

          self.closeInlineOverlay();

          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) cartDrawer.open();

          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: matchedVariant.id, quantity: 1 })
          })
          .then(function(r) { return r.json(); })
          .then(function(data) {
            if (!data.id && !data.items) return;
            if (typeof updateCartCounters === 'function') updateCartCounters();
            return fetch(window.location.pathname + '?sections=cart-drawer,cart-counter')
              .then(function(r) { return r.json(); })
              .then(function(sections) {
                if (cartDrawer) cartDrawer.renderContents({ sections }, false);
                if (typeof updateCartCounters === 'function') updateCartCounters();
                if (typeof updateFreeShipping === 'function') updateFreeShipping();
                // Manually init recommendations
                setTimeout(function() {
                  var recs = document.querySelector('#CartDrawer-Body product-recommendations');
                  if (recs && !recs._loaded && typeof recs.performRecommendations === 'function') {
                    recs.performRecommendations();
                  }
                }, 200);
              });
          })
          .catch(function() {});
        });
        btn._bound = true;
      });

      if (!this._overlayOutsideClickBound) {
        document.addEventListener('click', (e) => {
          if (this._activeOverlay && this._activeOverlay.classList.contains('is-active')) {
            if (!this._activeOverlay.contains(e.target) && !e.target.closest('.quick-cart-drawer__trigger')) {
              this.closeInlineOverlay();
            }
          }
        });
        this._overlayOutsideClickBound = true;
      }

      if (!overlay._backdropBound) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) this.closeInlineOverlay();
        });
        overlay._backdropBound = true;
      }
    }

    closeInlineOverlay() {
      if (this._activeOverlay) {
        this._activeOverlay.classList.remove('is-active');
        if (this._activeOverlay._originalParent && this._activeOverlay.parentElement === document.body) {
          if (this._activeOverlay._originalNextSibling) {
            this._activeOverlay._originalParent.insertBefore(this._activeOverlay, this._activeOverlay._originalNextSibling);
          } else {
            this._activeOverlay._originalParent.appendChild(this._activeOverlay);
          }
        }
        this._activeOverlay = null;
      }
    }

    setActiveMedia(id) {
      var mediaFound = Array.from(this.querySelectorAll('[data-media-id]')).find(function(media) {
        return Number(media.dataset.mediaId) === id;
      });
      if (mediaFound && this.sliderInstance) {
        this.sliderInstance.slideTo(Number(mediaFound.dataset.index));
      }
    }

    toggle() {
      if (!this.toggleState) { this.open(); } else { this.close(); }
    }

    open() {
      this.toggleState = true;
      var blocksEl = this.querySelector('.quick-cart-drawer__blocks');
      if (!blocksEl) return;

      var closeButton = blocksEl.querySelector('.button--close');
      if (closeButton) closeButton.setAttribute('tabindex', '0');

      // Reset any previous inline positioning
      blocksEl.style.cssText = '';

      if (this._openAsModal) {
        this._openAsModal = false;

        // Check if cart drawer is already open
        var cartDrawer = document.querySelector('cart-drawer');
        this._cartDrawerWasOpen = cartDrawer && cartDrawer.classList.contains('is-visible');

        // Temporarily disable the cart drawer's outside-click handler
        if (this._cartDrawerWasOpen && cartDrawer && cartDrawer.handleOutsideClick) {
          document.removeEventListener('click', cartDrawer.handleOutsideClick);
        }

        // Add classes to show the modal
        this.classList.add('is--open', 'is--modal');
        this.classList.remove('is--desktop-overlay');

        // Move header into main for correct positioning
        var header = this.querySelector('.quick-cart-drawer__header');
        var main = this.querySelector('.quick-cart-drawer__main');
        if (header && main && header.parentElement !== main) {
          main.appendChild(header);
        }

        // Body scroll lock
        if (!this._cartDrawerWasOpen) {
          document.querySelector('body').classList.add('overflow-hidden');
        }

        // Mobile: add drag handle and touch-to-dismiss
        if (window.innerWidth < 750) {
          this._setupMobileDrag(blocksEl);
        }

        // Init image swiper after paint
        var self = this;
        requestAnimationFrame(function() {
          setTimeout(function() {
            self._initModalSwiper();
          }, 80);
        });

      } else if (window.innerWidth >= 750 && this._triggerCard) {
        // DESKTOP: card overlay
        var cardRect = this._triggerCard.getBoundingClientRect();
        blocksEl.style.position = 'fixed';
        blocksEl.style.top = cardRect.top + 'px';
        blocksEl.style.left = cardRect.left + 'px';
        blocksEl.style.width = cardRect.width + 'px';
        blocksEl.style.height = cardRect.height + 'px';
        blocksEl.style.transform = 'none';
        blocksEl.style.opacity = '1';
        blocksEl.style.pointerEvents = 'auto';
        blocksEl.style.display = 'flex';
        blocksEl.style.flexDirection = 'column';
        blocksEl.style.justifyContent = 'flex-end';
        blocksEl.style.background = 'transparent';
        blocksEl.style.overflow = 'hidden';
        blocksEl.style.borderRadius = '0';
        blocksEl.style.boxShadow = 'none';
        this.classList.add('is--open', 'is--desktop-overlay');
      } else {
        // MOBILE: bottom sheet
        document.querySelector('body').classList.add('overflow-hidden');
        this.classList.add('is--open');
        this.classList.remove('is--desktop-overlay');

        // Mobile: add drag handle and touch-to-dismiss
        this._setupMobileDrag(blocksEl);
      }

      this.opened();
    }

    _initModalSwiper() {
      var swiperEl = this.querySelector('.quick-cart-drawer__media-swiper');
      if (!swiperEl) return;

      // Destroy old swiper
      if (swiperEl.swiper) {
        try { swiperEl.swiper.destroy(true, true); } catch(e) {}
      }

      // Remove old nav arrows
      swiperEl.querySelectorAll('.qcd-modal-nav').forEach(function(n) { n.remove(); });

      var isMobile = window.innerWidth < 750;
      var swiperConfig = {
        slidesPerView: isMobile ? 2.3 : 1,
        spaceBetween: isMobile ? 2 : 0,
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true
        }
      };

      // Desktop: add navigation arrows
      if (!isMobile) {
        var prevBtn = document.createElement('button');
        prevBtn.type = 'button';
        prevBtn.className = 'qcd-modal-nav qcd-modal-nav--prev';
        prevBtn.setAttribute('aria-label', 'Previous image');
        prevBtn.innerHTML = '<svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        swiperEl.appendChild(prevBtn);

        var nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'qcd-modal-nav qcd-modal-nav--next';
        nextBtn.setAttribute('aria-label', 'Next image');
        nextBtn.innerHTML = '<svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M1 1L7 7L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        swiperEl.appendChild(nextBtn);

        swiperConfig.navigation = {
          prevEl: prevBtn,
          nextEl: nextBtn
        };
      }

      new Swiper(swiperEl, swiperConfig);
    }

    _setupMobileDrag(blocksEl) {
      if (!blocksEl) return;

      // Add drag handle if not already present
      if (!blocksEl.querySelector('.qcd-drag-handle')) {
        var handle = document.createElement('div');
        handle.className = 'qcd-drag-handle';
        handle.innerHTML = '<span class="qcd-drag-handle__pill"></span>';
        blocksEl.insertBefore(handle, blocksEl.firstChild);
      }

      // Touch drag to dismiss
      var self = this;
      var startY = 0;
      var currentY = 0;
      var isDragging = false;

      function onTouchStart(e) {
        // Only start drag from handle or top area of blocks
        var touch = e.touches[0];
        var blockRect = blocksEl.getBoundingClientRect();
        // Allow drag from top 40px of the drawer (handle area)
        if (touch.clientY - blockRect.top > 40) return;
        startY = touch.clientY;
        currentY = startY;
        isDragging = true;
        blocksEl.style.transition = 'none';
      }

      function onTouchMove(e) {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        var deltaY = currentY - startY;
        if (deltaY < 0) deltaY = 0; // only allow dragging down
        blocksEl.style.transform = 'translateY(' + deltaY + 'px)';
      }

      function onTouchEnd() {
        if (!isDragging) return;
        isDragging = false;
        var deltaY = currentY - startY;
        blocksEl.style.transition = '';
        blocksEl.style.transform = '';

        // If dragged more than 80px down, close the drawer
        if (deltaY > 80) {
          self.close();
        }
      }

      // Remove any previous listeners
      if (blocksEl._dragHandlers) {
        blocksEl.removeEventListener('touchstart', blocksEl._dragHandlers.start);
        blocksEl.removeEventListener('touchmove', blocksEl._dragHandlers.move);
        blocksEl.removeEventListener('touchend', blocksEl._dragHandlers.end);
      }

      blocksEl._dragHandlers = {
        start: onTouchStart,
        move: onTouchMove,
        end: onTouchEnd
      };

      blocksEl.addEventListener('touchstart', onTouchStart, { passive: true });
      blocksEl.addEventListener('touchmove', onTouchMove, { passive: true });
      blocksEl.addEventListener('touchend', onTouchEnd, { passive: true });
    }

    close() {
      if (!this.toggleState && !this.classList.contains('is--open') && !this.classList.contains('is--modal')) return;

      var wasModal = this.classList.contains('is--modal');
      this.toggleState = false;

      var blocksEl = this.querySelector('.quick-cart-drawer__blocks');
      if (blocksEl) blocksEl.style.cssText = '';

      var shopTheLookDrawer = document.querySelector('shop-the-look-drawer');
      var cartDrawer = document.querySelector('cart-drawer');
      var cartStillOpen = cartDrawer && cartDrawer.classList.contains('is-visible');

      // Re-enable cart drawer outside-click handler
      if (this._cartDrawerWasOpen && cartDrawer && cartDrawer.handleOutsideClick) {
        setTimeout(function() {
          if (cartDrawer.classList.contains('is-visible')) {
            document.addEventListener('click', cartDrawer.handleOutsideClick);
          }
        }, 200);
      }

      // Body scroll
      if (!this.classList.contains('is--desktop-overlay') && !cartStillOpen) {
        if (shopTheLookDrawer && !shopTheLookDrawer.classList.contains('is--open')) {
          document.querySelector('body').classList.remove('overflow-hidden');
        }
        if (!shopTheLookDrawer) {
          document.querySelector('body').classList.remove('overflow-hidden');
        }
      }

      // If cart is open, maintain its scroll lock
      if (cartStillOpen) {
        document.querySelector('body').style.overflow = 'hidden';
      }

      // Move header back to blocks
      this._ensureHeaderInBlocks();

      // Destroy swiper + remove nav arrows
      var swiperEl = this.querySelector('.quick-cart-drawer__media-swiper');
      if (swiperEl) {
        swiperEl.querySelectorAll('.qcd-modal-nav').forEach(function(n) { n.remove(); });
        if (swiperEl.swiper) {
          try { swiperEl.swiper.destroy(true, true); } catch(e) {}
        }
      }

      // Clean up drag handle and touch listeners
      if (blocksEl) {
        var dragHandle = blocksEl.querySelector('.qcd-drag-handle');
        if (dragHandle) dragHandle.remove();
        if (blocksEl._dragHandlers) {
          blocksEl.removeEventListener('touchstart', blocksEl._dragHandlers.start);
          blocksEl.removeEventListener('touchmove', blocksEl._dragHandlers.move);
          blocksEl.removeEventListener('touchend', blocksEl._dragHandlers.end);
          blocksEl._dragHandlers = null;
        }
      }

      // Reset all flags
      this._cartDrawerWasOpen = false;
      this._openAsModal = false;

      // Remove ALL classes
      this.classList.remove('is--open', 'is--desktop-overlay', 'is--modal');

      this.closed();
      this.toggleAriaExpanded();

      // Trigger cart sync so recommendations update
      if (wasModal) {
        document.dispatchEvent(new Event('quick-cart:item-added'));
      }

      if (document.querySelector('card-product-slider.product--open-on-quick-cart')) {
        document.querySelector('card-product-slider.product--open-on-quick-cart').slider.autoplay.start();
        document.querySelector('card-product-slider.product--open-on-quick-cart').classList.remove('product--open-on-quick-cart');
      }
    }

    toggleAriaExpanded(event) {
      if (event) {
        if (event.target.closest('button')) event.target.closest('button').setAttribute('aria-expanded', true);
        var closeBtn = this.querySelector('.button--close');
        if (closeBtn) closeBtn.setAttribute('aria-expanded', true);
      } else {
        document.querySelectorAll('[aria-controls="quick-cart-drawer"]').forEach(function(button) {
          button.setAttribute('aria-expanded', false);
        });
      }
    }

    opened() {
      this.dispatchEvent(new Event('opened', { bubbles: true }));
    }

    closed() {
      this.dispatchEvent(new Event('closed', { bubbles: true }));
    }
  }

  customElements.define('quick-cart-drawer', QuickCartDrawer);
}
